import { IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { FlexBox, Inline } from '@ardoq/layout';
import { IHeaderParams } from 'ag-grid-enterprise';
import { InventoryContext } from '../types';
import { APIComponentAttributes } from '@ardoq/api-types';
import { MouseEvent } from 'react';
import styled from 'styled-components';
import { t } from '@ardoq/i18n';
import { getInsetFocusRingBorderCSS } from '@ardoq/style-helpers';
import { tourAnchorIds } from '@ardoq/product-tours';
import { collapseAllForGrid, expandAllForGrid } from '../serverSideRefresh';

// Horizontally align the first hierarchy button with chevron position in rows
const HierarchyButtonsWrapper = styled(Inline)`
  flex-shrink: 0;

  /* AG Grid header cells clip overflow, cutting off a button's default
     outset focus ring. Render it inset so it stays within the cell. */
  button:is(:focus-visible, .focus)::before {
    ${getInsetFocusRingBorderCSS()};
  }
`;

type NameHeaderWithHierarchyControlsProps = IHeaderParams<
  APIComponentAttributes,
  InventoryContext
>;

/**
 * Inner header for the Name column, holding the hierarchy Collapse/Expand all
 * buttons.
 *
 * Invariant: what this component renders must never depend on `props.context`.
 *
 * AG Grid instantiates header components while the grid is being constructed,
 * but the inventory context is installed later, on grid ready, in
 * `initializeInventoryAgGrid`. The first render therefore always sees
 * `context: undefined` — and nothing recreates the header afterwards:
 * `Inventory.tsx`'s `refreshHeader()` runs before the grid is ready on mount,
 * and then only when `isHierarchyEnabled` actually flips. Refreshing at grid
 * ready does not help either: this component's React portal is mounted on a
 * timer (ag-grid-react's `PortalManager.mountReactPortal`), so at that point
 * `HeaderComp.refresh()` finds no inner component yet and silently does
 * nothing.
 *
 * Anything that decides *whether* something renders must therefore arrive
 * through the column definition, as `innerHeaderComponentParams` in
 * `mapReportColumnDefinitionToAgGridFormat` — AG Grid applies those from the
 * `columnDefs` prop, which is re-derived whenever the inputs change. Reading
 * the context is only acceptable where a missing value degrades gracefully:
 * the label falls back to its default English string until the next header
 * refresh, and the tracking commands are skipped for a click on a header that
 * still has no context.
 *
 * Breaking this hides the buttons until the user toggles hierarchy off and on
 * again — ARD-32945, and then ARD-34290.
 */
export const NameHeaderWithHierarchyControls = (
  props: NameHeaderWithHierarchyControlsProps
) => {
  const i18nLocale = props.context?.i18nLocale ?? {};

  const handleCollapseAll = (event: MouseEvent<HTMLButtonElement>): void => {
    collapseAllForGrid(props.api);
    props.context?.commands.notifyCollapseAllInHierarchy();
    event.stopPropagation(); // to not trigger sorting when clicking the collapse button
  };

  const handleExpandAll = (event: MouseEvent<HTMLButtonElement>): void => {
    expandAllForGrid(props.api);
    props.context?.commands.notifyExpandAllInHierarchy();
    event.stopPropagation(); // to not trigger sorting when clicking the expand button
  };

  return (
    <FlexBox align="center" gap="xsmall">
      <HierarchyButtonsWrapper align="center">
        <IconButton
          iconName={IconName.UNFOLD_LESS}
          label={t(
            i18nLocale,
            'inventoryApp:ag.header.collapseAll',
            'Collapse all'
          )}
          onClick={handleCollapseAll}
        />
        <IconButton
          iconName={IconName.UNFOLD_MORE}
          label={t(
            i18nLocale,
            'inventoryApp:ag.header.expandAll',
            'Expand all'
          )}
          onClick={handleExpandAll}
          data-tour-id={tourAnchorIds.inventoryExpandAll}
        />
      </HierarchyButtonsWrapper>
      <span>{props.displayName}</span>
    </FlexBox>
  );
};
